import React, { createContext, useReducer, useContext, useEffect } from 'react';
import type { ViewType, User } from '../types.ts';

type Theme = 'light'; // Only light theme is supported now

// State shape
interface GlobalState {
  activeView: ViewType;
  viewProps: any;
  theme: Theme;
  hiddenFeatures: string[];
  isAuthenticated: boolean;
  user: User | null;
}

// Action types
type Action =
  | { type: 'SET_VIEW'; payload: { view: ViewType, props?: any } }
  | { type: 'TOGGLE_FEATURE_VISIBILITY'; payload: { featureId: string } }
  | { type: 'LOGIN'; payload: User }
  | { type: 'LOGOUT' };


const initialState: GlobalState = {
  activeView: 'ai-command-center',
  viewProps: {},
  theme: 'light',
  hiddenFeatures: [],
  isAuthenticated: false,
  user: null,
};

const reducer = (state: GlobalState, action: Action): GlobalState => {
  switch (action.type) {
    case 'SET_VIEW':
      // Reset viewProps on navigation unless new ones are provided
      return { ...state, activeView: action.payload.view, viewProps: action.payload.props || {} };
    case 'TOGGLE_FEATURE_VISIBILITY': {
        const { featureId } = action.payload;
        const isHidden = state.hiddenFeatures.includes(featureId);
        const newHiddenFeatures = isHidden
            ? state.hiddenFeatures.filter(id => id !== featureId)
            : [...state.hiddenFeatures, featureId];
        return { ...state, hiddenFeatures: newHiddenFeatures };
    }
    case 'LOGIN':
        return { ...state, isAuthenticated: true, user: action.payload };
    case 'LOGOUT':
        return {
            ...initialState,
            theme: state.theme, // Persist theme
            hiddenFeatures: state.hiddenFeatures, // Persist hidden features
            isAuthenticated: false,
            user: null,
        };
    default:
      return state;
  }
};

const GlobalStateContext = createContext<{
  state: GlobalState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null,
});

const LOCAL_STORAGE_KEY = 'devcore_snapshot';
const CONSENT_KEY = 'devcore_ls_consent';

export const GlobalStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const canPersist = (() => {
        try {
            return localStorage.getItem(CONSENT_KEY) === 'granted';
        } catch (e) {
            return false;
        }
    })();

    const [state, dispatch] = useReducer(reducer, initialState, (initial) => {
        const hydratedState = { ...initial };
        if (!canPersist) return hydratedState;
        
        try {
            const storedStateJSON = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (!storedStateJSON) return hydratedState;
            
            const storedState = JSON.parse(storedStateJSON);
            
            if (storedState.isAuthenticated && storedState.user) {
                hydratedState.isAuthenticated = true;
                hydratedState.user = storedState.user;
            }
            if (storedState.activeView) {
                hydratedState.activeView = storedState.activeView;
            }
            if (storedState.hiddenFeatures) {
                hydratedState.hiddenFeatures = storedState.hiddenFeatures;
            }

            return hydratedState;
        } catch (error) {
            console.error("Failed to parse state from localStorage", error);
            return hydratedState;
        }
    });

    useEffect(() => {
        if (!canPersist) return;

        // Debounce saving to localStorage to avoid performance issues
        const handler = setTimeout(() => {
            try {
                // Persist only necessary and non-sensitive state
                const stateToSave = { 
                    isAuthenticated: state.isAuthenticated,
                    user: state.user,
                    activeView: state.activeView,
                    hiddenFeatures: state.hiddenFeatures,
                };
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave));
            } catch (error) {
                console.error("Failed to save state to localStorage", error);
            }
        }, 500); // Save state 500ms after the last change
        
        return () => clearTimeout(handler);
    }, [state, canPersist]);


    return (
        <GlobalStateContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export const useGlobalState = () => useContext(GlobalStateContext);
